import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { User, Clock, ArrowUp, Bookmark, FileText, Star, Sparkles, ChevronDown, ChevronRight, Key, Copy, RefreshCw, Eye, EyeOff, Code, Terminal } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = 'https://promptvault.org';

export default function Profile() {
  const { userId } = useParams();
  const { user: currentUser, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [byCategory, setByCategory] = useState({});
  const [expandedCats, setExpandedCats] = useState({});
  const [loading, setLoading] = useState(true);

  // API token state (own profile only)
  const [apiToken, setApiToken] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [showApiDocs, setShowApiDocs] = useState(false);

  const isOwnProfile = currentUser?.id === userId;

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, promptsRes] = await Promise.all([
          axios.get(`${API}/users/${userId}`),
          axios.get(`${API}/users/${userId}/prompts`)
        ]);
        setProfile(profileRes.data);
        setPrompts(promptsRes.data.prompts);
        setByCategory(promptsRes.data.by_category);
        const expanded = {};
        Object.keys(promptsRes.data.by_category).forEach(k => expanded[k] = true);
        setExpandedCats(expanded);
      } catch {}
      setLoading(false);
    };
    load();
  }, [userId]);

  // Load API token for own profile
  useEffect(() => {
    if (isOwnProfile && token) {
      axios.get(`${API}/user/api-token`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setApiToken(r.data.api_token))
        .catch(() => {});
    }
  }, [isOwnProfile, token]);

  const generateToken = async () => {
    setTokenLoading(true);
    try {
      const res = await axios.post(`${API}/user/api-token`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setApiToken(res.data.api_token);
      setShowToken(true);
      toast.success(apiToken ? 'API token regenerated' : 'API token generated');
    } catch { toast.error('Failed to generate token'); }
    setTokenLoading(false);
  };

  const copyToken = () => {
    navigator.clipboard.writeText(apiToken);
    toast.success('API token copied');
  };

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground">Loading...</div>;
  if (!profile) return <div className="flex justify-center py-20 text-muted-foreground">User not found</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6" data-testid="profile-page">
      {/* Profile Header */}
      <Card className="p-6 sm:p-8 border-border/50">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-2xl font-bold shrink-0" data-testid="profile-avatar">
            {profile.username?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold" data-testid="profile-username">{profile.username}</h1>
            {profile.bio && <p className="text-sm text-muted-foreground mt-1">{profile.bio}</p>}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
              <span className="flex items-center gap-1.5 text-amber-500 font-semibold" data-testid="profile-karma">
                <Star className="w-4 h-4 fill-amber-500" /> {profile.karma} Karma
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="profile-prompts-count">
                <FileText className="w-3.5 h-3.5" /> {profile.prompt_count} Prompts
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="profile-upvotes">
                <ArrowUp className="w-3.5 h-3.5" /> {profile.total_upvotes} Upvotes
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="profile-bookmarks">
                <Bookmark className="w-3.5 h-3.5" /> {profile.bookmark_count} Bookmarked
              </span>
            </div>
            {profile.role === 'admin' && <Badge className="mt-2 rounded-full">Admin</Badge>}
          </div>
        </div>
      </Card>

      {/* API Access Section — own profile only */}
      {isOwnProfile && (
        <Card className="p-6 sm:p-8 border-border/50" data-testid="api-section">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" /> API Access
          </h2>

          {/* Token display / generate */}
          {apiToken ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 font-mono text-sm bg-muted/50 border border-border rounded-lg px-4 py-2.5 truncate" data-testid="api-token-display">
                  {showToken ? apiToken : '•'.repeat(32)}
                </div>
                <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setShowToken(!showToken)} data-testid="toggle-token-visibility">
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="shrink-0" onClick={copyToken} data-testid="copy-token-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs" onClick={generateToken} disabled={tokenLoading} data-testid="regenerate-token-btn">
                  <RefreshCw className={`w-3 h-3 ${tokenLoading ? 'animate-spin' : ''}`} /> Regenerate
                </Button>
                <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs" onClick={() => setShowApiDocs(!showApiDocs)} data-testid="toggle-api-docs-btn">
                  <Code className="w-3 h-3" /> {showApiDocs ? 'Hide' : 'Show'} API Docs
                </Button>
                {profile?.api_prompt_count > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {profile.api_prompt_count} API prompts (earns 1 karma per 10)
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">Generate an API token to add prompts programmatically.</p>
              <Button onClick={generateToken} disabled={tokenLoading} className="rounded-full gap-2" data-testid="generate-token-btn">
                <Key className="w-4 h-4" /> {tokenLoading ? 'Generating...' : 'Generate API Token'}
              </Button>
            </div>
          )}

          {/* API Documentation */}
          {showApiDocs && (
            <div className="mt-5 space-y-4" data-testid="api-docs">
              <Separator />
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" /> API Documentation</h3>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1.5">BASE URL</p>
                  <code className="block text-xs bg-muted/50 border border-border rounded-lg px-3 py-2 font-mono">{BASE_URL}/api/v1</code>
                </div>

                <div>
                  <p className="text-xs font-semibold text-muted-foreground mb-1.5">AUTHENTICATION</p>
                  <code className="block text-xs bg-muted/50 border border-border rounded-lg px-3 py-2 font-mono">Authorization: Bearer {'<your_api_token>'}</code>
                </div>

                <div>
                  <p className="text-xs font-semibold text-primary mb-1.5">POST /v1/users — Create User</p>
                  <pre className="text-xs bg-zinc-900 text-zinc-100 rounded-lg px-4 py-3 overflow-x-auto font-mono whitespace-pre">{`curl -X POST ${BASE_URL}/api/v1/users \\
  -H "Content-Type: application/json" \\
  -d '{
    "username": "johndoe",
    "email": "john@example.com",
    "password": "securepassword"
  }'`}</pre>
                </div>

                <div>
                  <p className="text-xs font-semibold text-primary mb-1.5">POST /v1/prompts — Add Prompt</p>
                  <pre className="text-xs bg-zinc-900 text-zinc-100 rounded-lg px-4 py-3 overflow-x-auto font-mono whitespace-pre">{`curl -X POST ${BASE_URL}/api/v1/prompts \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${apiToken ? apiToken.slice(0, 8) + '...' : '<token>'}" \\
  -d '{
    "title": "My Awesome Prompt",
    "content": "You are a helpful assistant...",
    "categories": ["Programming", "Content"],
    "platform_id": ""
  }'`}</pre>
                </div>

                <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold">Notes:</p>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>API prompts automatically shoutout related low-karma prompts</li>
                    <li>Karma earned: <strong>1 karma per 10 prompts</strong> added via API</li>
                    <li>Rate limit: 100 prompts per hour</li>
                    <li>Keep your API token secret — regenerate if compromised</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Shoutouts Section */}
      {((profile.shoutouts_made?.length > 0) || (profile.shoutouts_received?.length > 0)) && (
        <Card className="p-6 sm:p-8 border-border/50" data-testid="shoutouts-section">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> Shoutouts
          </h2>

          {profile.shoutouts_made?.length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Shoutouts Given ({profile.shoutouts_made.length})</h3>
              <div className="space-y-2" data-testid="shoutouts-made-list">
                {profile.shoutouts_made.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50 text-sm">
                    <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <Link to={`/prompt/${s.slug || s.id}`} className="font-medium hover:text-primary truncate">{s.title}</Link>
                    <span className="text-muted-foreground shrink-0">shouted out</span>
                    {s.shoutout_target && (
                      <Link to={`/prompt/${s.shoutout_target.slug || s.shoutout_target.id}`} className="font-medium hover:text-primary truncate">{s.shoutout_target.title}</Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {profile.shoutouts_received?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-2">Shoutouts Received ({profile.shoutouts_received.length})</h3>
              <div className="space-y-2" data-testid="shoutouts-received-list">
                {profile.shoutouts_received.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-500/20 text-sm">
                    <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <Link to={`/prompt/${s.slug || s.id}`} className="font-medium hover:text-primary truncate">{s.title}</Link>
                    <span className="text-muted-foreground shrink-0">by {s.username}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Prompts by Category */}
      <Card className="p-6 sm:p-8 border-border/50" data-testid="prompts-by-category">
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" /> Prompts by Category
        </h2>

        {Object.keys(byCategory).length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No prompts shared yet.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(byCategory).map(([cat, catPrompts]) => (
              <div key={cat} className="border border-border/50 rounded-lg overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors text-left"
                  onClick={() => toggleCat(cat)}
                  data-testid={`category-toggle-${cat}`}
                >
                  <span className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-full text-xs">{cat}</Badge>
                    <span className="text-sm text-muted-foreground">{catPrompts.length} prompt{catPrompts.length !== 1 ? 's' : ''}</span>
                  </span>
                  {expandedCats[cat] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {expandedCats[cat] && (
                  <div className="border-t border-border/50">
                    {catPrompts.map(p => (
                      <Link
                        key={p.id}
                        to={`/prompt/${p.slug || p.id}`}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-accent/30 transition-colors border-b border-border/30 last:border-b-0"
                        data-testid={`profile-prompt-${p.id}`}
                      >
                        <span className="text-sm font-medium truncate flex-1">{p.title}</span>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0 ml-3">
                          <span className="flex items-center gap-1"><ArrowUp className="w-3 h-3" />{p.upvotes || 0}</span>
                          <span>{new Date(p.created_at).toLocaleDateString()}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
